import { useState, useEffect } from "react";
import dayjs from "dayjs";
import routes from "@/routes";
import { useNavigate } from "react-router-dom";

import {
  Form,
  Modal,
  Input,
  Flex,
  Button,
  Typography,
  notification,
} from "antd";
import CustomSelect from "@/components/inputs/CustomSelect";
import { getEmployeeUnitsOptions } from "@/api/units";
import { createItemFromTable } from "@/api/tasks";
import { getPluralizedWord } from "@/utils";

// Закомментировано по просьбе клиента не удалять
// const dateFormat = "DD.MM.YYYY";
// const dateTimeFormat = "DD.MM.YYYY HH:mm";

// const buildDatePayload = ({ fromField, value, field_uuid, format = false }) => {
//   if ((!fromField && !value) || (fromField && !field_uuid)) {
//     return {
//       mode: "null",
//       value: null,
//       field_uuid: null,
//     };
//   }

//   if (fromField) {
//     return {
//       mode: "field",
//       value: null,
//       field_uuid,
//     };
//   }

//   return {
//     mode: "value",
//     value: format ? value.format().split("T")[0] : value.format(),
//     field_uuid: null,
//   };
// };

const { Text } = Typography;
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const buildPreviewTitle = ({ prefix, parentName, rowName }) =>
  [prefix?.trim(), parentName, rowName].filter(Boolean).join(". ");

// Окно для создания/редактирования задачи из таблицы ресурса, содержит форму
const TableTaskModal = ({
  isAdmins = false,
  data,
  //dateFields = [],
  selectedRowUuids = [],
  visible,
  onSave,
  onCancel,
  canEditUnit,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const namePrefix = Form.useWatch("name_prefix", form);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  // Закомментировано по просьбе клиента не удалять
  // const [startFromField, setStartFromField] = useState(false);
  // const [endFromField, setEndFromField] = useState(false);

  // useEffect(() => {
  //   if (startFromField) {
  //     form.resetFields(["start_date"]);
  //   } else {
  //     form.resetFields(["start_field"]);
  //   }
  // }, [startFromField, form]);

  // useEffect(() => {
  //   if (endFromField) {
  //     form.resetFields(["end_date"]);
  //   } else {
  //     form.resetFields(["end_field"]);
  //   }
  // }, [endFromField, form]);

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;

    const loadUnits = async () => {
      setUnitsLoading(true);
      try {
        const { units } = await getEmployeeUnitsOptions();
        if (!cancelled) {
          setUnits(units || []);
        }
      } finally {
        if (!cancelled) {
          setUnitsLoading(false);
        }
      }
    };

    loadUnits();

    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (!units.length) return;

    const validUnit =
      data?.unit_uuid && units.some((u) => u.uuid === data.unit_uuid)
        ? data.unit_uuid
        : (units[0]?.uuid ?? null);

    form.setFieldValue("unit_uuid", validUnit);
  }, [units, data, form]);

  //const canUseFieldMode = dateFields.length > 0;
  const previewTitle = buildPreviewTitle({
    prefix: namePrefix,
    parentName: data?.object_name || "[Наименование родительского ресурса]",
    rowName: "[Наименование строки]",
  });
  const rowUuids = selectedRowUuids.length
    ? selectedRowUuids
    : data?.row_uuids || [];
  const createdTasksCount = rowUuids.length;

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    if (!rowUuids.length) {
      setError("Выберите строки для создания задач");
      return;
    }

    setIsLoading(true);

    const res = {
      ...data,

      // Закомментировано по просьбе клиента не удалять
      // start_date: buildDatePayload({
      //   fromField: startFromField,
      //   value: values?.start_date,
      //   field_uuid:
      //     dateFields.find((m) => m.uuid === values?.start_field)?.field ??
      //     null,
      //   format: true,
      // }),

      // due_date: buildDatePayload({
      //   fromField: endFromField,
      //   value: values?.end_date,
      //   field_uuid:
      //     dateFields.find((m) => m.uuid === values?.end_field)?.field ?? null,
      // }),

      start_date: dayjs().format("YYYY-MM-DD"),
      time_zone: userTimeZone,
      name_prefix: values?.name_prefix?.trim() || null,
      unit_uuid: values?.unit_uuid,
      row_uuids: rowUuids,
    };

    try {
      const newTasks = await createItemFromTable(res);

      if (newTasks?.length > 0) {
        const isSingleTask = newTasks.length === 1;
        const tableName = data?.definition_name;

        const params = new URLSearchParams();
        newTasks.forEach((item) => {
          params.append("uuid", item);
        });

        notification.open({
          message: (
            <div style={{ fontWeight: 500 }}>
              {isSingleTask ? "Задача создана" : "Задачи созданы"}
            </div>
          ),
          description: (
            <div style={{ whiteSpace: "normal", lineHeight: "1.4em" }}>
              {`${isSingleTask ? "Задача" : "Задачи"} по таблице ${tableName} ${isSingleTask ? "добавлена" : "добавлены"}`}
              <span
                style={{ color: "#1677ff", cursor: "pointer", marginLeft: 4 }}
                onClick={() =>
                  navigate({
                    pathname: isAdmins
                      ? routes.administration.tasks
                      : routes.tasks,
                    search: `?${params.toString()}`,
                  })
                }
              >
                в список задач
              </span>
            </div>
          ),
          icon: null,
          duration: 4,
          pauseOnHover: true,
          placement: "bottomRight",
          style: {
            border: "1px solid #B7EB8F",
            background: "#f6ffed",
            borderRadius: "8px",
            width: "500px",
          },
        });
      }

      onSave();
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }

    setIsLoading(false);
  };

  return (
    <>
      <Modal
        open={visible}
        title="Создать задачи"
        onOk={handleSave}
        onCancel={handleCancel}
        footer={
          <Flex justify="space-between">
            <Button key="back" onClick={handleCancel}>
              Отмена
            </Button>

            <Flex gap={10}>
              <Button
                key="submit"
                type="primary"
                loading={isLoading || unitsLoading}
                onClick={() => {
                  form
                    .validateFields()
                    .then((values) => {
                      handleSave(values);
                    })
                    .catch((info) => {
                      console.log("Ошибка валидации:", info);
                    });
                }}
              >
                Сохранить
              </Button>
            </Flex>
          </Flex>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{}}
          onFinish={handleSave}
        >
          <Form.Item>
            <Text>{`Будет создано ${createdTasksCount} ${getPluralizedWord(
              createdTasksCount,
              ["задача", "задачи", "задач"],
            )}`}</Text>
          </Form.Item>

          <Form.Item
            name="unit_uuid"
            label="Рабочая группа"
            rules={[{ required: true }]}
          >
            <CustomSelect
              options={units}
              loading={unitsLoading}
              fieldNames={{ label: "name", value: "uuid" }}
              disabled={!canEditUnit}
              placeholder={
                unitsLoading ? "Загрузка..." : "Выберите рабочую группу"
              }
            />
          </Form.Item>

          {/* <Form.Item label="Дата начала"> // Закомментировано по просьбе клиента не удалять
            <Flex vertical gap={8}>
              {canUseFieldMode && (
                <Flex align="center" gap={8}>
                  <Switch
                    checked={startFromField}
                    onChange={(checked) => setStartFromField(checked)}
                  />
                  <span>Использовать поле объекта</span>
                </Flex>
              )}

              {startFromField ? (
                <Form.Item name="start_field" noStyle>
                  <CustomSelect
                    options={dateFields}
                    fieldNames={{ label: "name", value: "uuid" }}
                    placeholder="Выберите поле"
                    allowClear
                  />
                </Form.Item>
              ) : (
                <Form.Item name="start_date" noStyle>
                  <DatePicker format={dateFormat} style={{ width: "100%" }} />
                </Form.Item>
              )}
            </Flex>
          </Form.Item> */}

          <Form.Item
            name="name_prefix"
            label="Префикс названия"
            rules={[
              {
                max: 255,
                message: "Префикс не должен быть длиннее 255 символов",
              },
            ]}
          >
            <Input allowClear placeholder="Введите префикс" />
          </Form.Item>

          {/* <Form.Item label="Дата конца"> // Закомментировано по просьбе клиента не удалять
            <Flex vertical gap={8}>
              {canUseFieldMode && (
                <Flex align="center" gap={8}>
                  <Switch
                    checked={endFromField}
                    onChange={(checked) => setEndFromField(checked)}
                  />
                  <span>Использовать поле объекта</span>
                </Flex>
              )}

              {endFromField ? (
                <Form.Item name="end_field" noStyle>
                  <CustomSelect
                    options={dateFields}
                    fieldNames={{ label: "name", value: "uuid" }}
                    placeholder="Выберите поле"
                    allowClear
                  />
                </Form.Item>
              ) : (
                <Form.Item name="end_date" noStyle>
                  <DatePicker
                    format={dateTimeFormat}
                    showTime={{ defaultValue: dayjs("18:00", "HH:mm") }}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
            </Flex>
            </Form.Item> */}

          <Form.Item label="Предпросмотр названия задачи">
            <Text>{previewTitle}</Text>
          </Form.Item>

          {error && <Text type="danger">{error}</Text>}
        </Form>
      </Modal>
    </>
  );
};

export default TableTaskModal;
